<app-page-layout 
  [title]="title"
  [subtitle]="subtitle"
  [heroImage]="heroImage"
  [breadcrumbs]="breadcrumbs"
  [prevPage]="prevPage"
  [nextPage]="nextPage">
  
  <!-- Introduction -->
  <section class="page-section">
    <div class="highlight-box">
      <h2><i class="pi pi-file-edit"></i> Objet et champ d'application</h2>
      
      <p>Les présentes Conditions Générales de Vente (CGV) régissent les relations contractuelles entre <strong>BROKINS SAS</strong>, courtier en assurance immatriculé à l'ORIAS sous le numéro <strong>16 004 540</strong>, et ses clients (ci-après "le Client").</p>
      
      <p>Elles s'appliquent à l'ensemble des services de courtage en assurance proposés par Brokins, notamment :</p>
      
      <ul>
        <li>La comparaison d'offres d'assurance</li>
        <li>Le conseil en assurance</li>
        <li>L'intermédiation pour la souscription de contrats</li>
        <li>La gestion et le suivi des contrats</li>
      </ul>
      
      <div class="update-info">
        <i class="pi pi-calendar"></i>
        <div>
          <p><strong>Date d'entrée en vigueur :</strong> Décembre 2025<br>
          <strong>Version :</strong> 2.0</p>
        </div>
      </div>
    </div>
  </section>

  <!-- Définitions -->
  <section class="page-section">
    <h2><i class="pi pi-book"></i> Définitions</h2>
    
    <div class="definitions-list">
      <div class="definition-item">
        <h3>Courtier</h3>
        <p>Désigne BROKINS SAS, société de courtage en assurance immatriculée à l'ORIAS sous le numéro 16 004 540.</p>
      </div>
      
      <div class="definition-item">
        <h3>Client</h3>
        <p>Toute personne physique ou morale ayant recours aux services de Brokins.</p>
      </div>
      
      <div class="definition-item">
        <h3>Assureur</h3>
        <p>Compagnie d'assurance partenaire de Brokins proposant des contrats d'assurance.</p>
      </div>
      
      <div class="definition-item">
        <h3>Contrat</h3>
        <p>Contrat d'assurance souscrit par le Client auprès d'un Assureur par l'intermédiaire de Brokins.</p>
      </div>
      
      <div class="definition-item">
        <h3>Devis</h3>
        <p>Proposition d'assurance établie par un Assureur suite à une demande formulée via Brokins.</p>
      </div>
    </div>
  </section>

  <!-- Services proposés -->
  <section class="page-section">
    <h2><i class="pi pi-briefcase"></i> Services proposés</h2>
    
    <div class="services-grid">
      <div class="service-card">
        <i class="pi pi-search"></i>
        <h3>Comparaison d'assurances</h3>
        <p>Analyse comparative des offres d'assurance disponibles sur le marché selon vos besoins et critères.</p>
        <span class="service-tag free">Gratuit et sans engagement</span>
      </div>
      
      <div class="service-card">
        <i class="pi pi-comments"></i>
        <h3>Conseil personnalisé</h3>
        <p>Accompagnement par nos experts pour identifier les garanties adaptées à votre situation.</p>
        <span class="service-tag free">Gratuit et sans engagement</span>
      </div>
      
      <div class="service-card">
        <i class="pi pi-file-edit"></i>
        <h3>Intermédiation</h3>
        <p>Transmission de vos demandes aux assureurs et négociation des conditions tarifaires.</p>
        <span class="service-tag free">Gratuit et sans engagement</span>
      </div>
      
      <div class="service-card">
        <i class="pi pi-cog"></i>
        <h3>Gestion de contrats</h3>
        <p>Assistance pour la souscription, les modifications et la gestion administrative de vos contrats.</p>
        <span class="service-tag free">Gratuit et sans engagement</span>
      </div>
      
      <div class="service-card">
        <i class="pi pi-sync"></i>
        <h3>Suivi et renouvellement</h3>
        <p>Accompagnement tout au long de la vie de vos contrats et aux échéances.</p>
        <span class="service-tag free">Gratuit et sans engagement</span>
      </div>
      
      <div class="service-card">
        <i class="pi pi-phone"></i>
        <h3>Support client</h3>
        <p>Assistance téléphonique et par email pour toutes vos questions.</p>
        <span class="service-tag included">Inclus</span>
      </div>
    </div>
  </section>

  <!-- Rémunération -->
  <section class="page-section">
    <div class="info-box">
      <h2><i class="pi pi-euro"></i> Rémunération du courtier</h2>
      
      <p>Conformément à la réglementation, Brokins informe ses clients des modalités de rémunération :</p>
      
      <div class="remuneration-info">
        <h3><i class="pi pi-check-circle"></i> Services gratuits pour le client</h3>
        <p>Tous nos services (comparaison, conseil, intermédiation, gestion) sont <strong>entièrement gratuits</strong> pour le Client. Aucune facturation directe n'est effectuée.</p>
        
        <h3><i class="pi pi-wallet"></i> Commission versée par les assureurs</h3>
        <p>Brokins perçoit une commission de la part des assureurs partenaires lors de la souscription d'un contrat. Cette commission :</p>
        <ul>
          <li>Est incluse dans la prime d'assurance payée par le Client à l'assureur</li>
          <li>N'entraîne aucun surcoût pour le Client</li>
          <li>Varie selon les assureurs et les types de contrats</li>
          <li>Est encadrée par les conventions signées avec chaque assureur</li>
        </ul>
        
        <h3><i class="pi pi-info-circle"></i> Transparence</h3>
        <p>Le montant exact de la commission applicable à votre contrat peut vous être communiqué sur simple demande avant la souscription conformément à l'article L.511-4 du Code des assurances.</p>
      </div>
    </div>
  </section>

  <!-- Obligations du courtier -->
  <section class="page-section">
    <h2><i class="pi pi-shield"></i> Obligations du courtier</h2>
    
    <div class="obligations-list">
      <div class="obligation-item">
        <i class="pi pi-user-edit"></i>
        <div>
          <h3>Devoir de conseil</h3>
          <p>Brokins s'engage à fournir un conseil personnalisé et adapté à votre situation, en tenant compte de vos besoins, de votre budget et de vos exigences.</p>
        </div>
      </div>
      
      <div class="obligation-item">
        <i class="pi pi-info-circle"></i>
        <div>
          <h3>Information claire et transparente</h3>
          <p>Nous nous engageons à vous informer de manière claire sur les caractéristiques des contrats, les garanties, les exclusions, les franchises et les tarifs.</p>
        </div>
      </div>
      
      <div class="obligation-item">
        <i class="pi pi-eye"></i>
        <div>
          <h3>Transparence sur la rémunération</h3>
          <p>Communication des modalités de rémunération et du montant des commissions sur demande.</p>
        </div>
      </div>
      
      <div class="obligation-item">
        <i class="pi pi-lock"></i>
        <div>
          <h3>Confidentialité</h3>
          <p>Protection et confidentialité de toutes les informations personnelles communiquées, conformément au RGPD.</p>
        </div>
      </div>
      
      <div class="obligation-item">
        <i class="pi pi-clock"></i>
        <div>
          <h3>Réactivité</h3>
          <p>Traitement de vos demandes dans les meilleurs délais et réponse sous 48h ouvrées maximum.</p>
        </div>
      </div>
      
      <div class="obligation-item">
        <i class="pi pi-file"></i>
        <div>
          <h3>Remise des documents</h3>
          <p>Transmission de tous les documents contractuels (conditions générales, particulières, notices d'information, etc.).</p>
        </div>
      </div>
    </div>
  </section>

  <!-- Obligations du client -->
  <section class="page-section">
    <h2><i class="pi pi-user"></i> Obligations du client</h2>
    
    <div class="client-obligations">
      <p>Le Client s'engage à :</p>
      
      <ul>
        <li><strong>Fournir des informations exactes et complètes</strong> lors de ses demandes de devis et de souscription</li>
        <li><strong>Déclarer tous les éléments</strong> susceptibles d'influencer l'appréciation du risque par l'assureur</li>
        <li><strong>Informer Brokins de tout changement</strong> de situation pouvant modifier les conditions du contrat</li>
        <li><strong>Payer les primes d'assurance</strong> aux échéances convenues avec l'assureur</li>
        <li><strong>Lire attentivement</strong> les conditions générales et particulières avant souscription</li>
        <li><strong>Respecter les obligations</strong> prévues dans les contrats d'assurance souscrits</li>
      </ul>
      
      <div class="warning-box">
        <i class="pi pi-exclamation-triangle"></i>
        <div>
          <h4>Important</h4>
          <p>Toute fausse déclaration intentionnelle ou réticence peut entraîner la nullité du contrat d'assurance conformément à l'article L.113-8 du Code des assurances.</p>
        </div>
      </div>
    </div>
  </section>

  <!-- Procédure de souscription -->
  <section class="page-section">
    <h2><i class="pi pi-list-check"></i> Procédure de souscription</h2>
    
    <div class="procedure-steps">
      <div class="step">
        <div class="step-number">1</div>
        <div class="step-content">
          <h3>Demande de devis</h3>
          <p>Le Client remplit un questionnaire en ligne ou contacte un conseiller Brokins pour obtenir des devis personnalisés.</p>
        </div>
      </div>
      
      <div class="step">
        <div class="step-number">2</div>
        <div class="step-content">
          <h3>Comparaison et conseil</h3>
          <p>Brokins compare les offres disponibles et conseille le Client sur les solutions les plus adaptées.</p>
        </div>
      </div>
      
      <div class="step">
        <div class="step-number">3</div>
        <div class="step-content">
          <h3>Choix et souscription</h3>
          <p>Le Client choisit l'offre qui lui convient et valide sa souscription. Brokins transmet la demande à l'assureur.</p>
        </div>
      </div>
      
      <div class="step">
        <div class="step-number">4</div>
        <div class="step-content">
          <h3>Validation et paiement</h3>
          <p>L'assureur valide la souscription et émet les documents contractuels. Le Client effectue le premier règlement.</p>
        </div>
      </div>
      
      <div class="step">
        <div class="step-number">5</div>
        <div class="step-content">
          <h3>Mise en place du contrat</h3>
          <p>Le contrat prend effet à la date convenue. Le Client reçoit ses documents d'assurance (attestation, carte verte, etc.).</p>
        </div>
      </div>
    </div>
  </section>

  <!-- Droit de rétractation -->
  <section class="page-section">
    <div class="highlight-box">
      <h2><i class="pi pi-undo"></i> Droit de rétractation</h2>
      
      <p>Conformément à l'article L.112-2-1 du Code des assurances et au Code de la consommation :</p>
      
      <div class="retractation-info">
        <h3><i class="pi pi-calendar"></i> Délai de rétractation</h3>
        <p>Le Client dispose d'un <strong>délai de 14 jours calendaires</strong> pour se rétracter de sa souscription, sans avoir à justifier de motifs ni à supporter de pénalités.</p>
        
        <h3><i class="pi pi-clock"></i> Point de départ du délai</h3>
        <p>Le délai court à compter :</p>
        <ul>
          <li>De la conclusion du contrat, ou</li>
          <li>De la réception des conditions contractuelles et des informations si cette date est postérieure</li>
        </ul>
        
        <h3><i class="pi pi-send"></i> Modalités</h3>
        <p>La rétractation doit être notifiée par lettre recommandée avec accusé de réception ou tout autre support durable à :</p>
        <ul>
          <li>L'assureur (coordonnées mentionnées dans le contrat)</li>
          <li>Copie à Brokins : contact&#64;brokins.fr</li>
        </ul>
        
        <h3><i class="pi pi-dollar"></i> Remboursement</h3>
        <p>En cas de rétractation, l'assureur rembourse la prime versée dans un délai de 30 jours. Si le contrat a déjà pris effet, la prime au prorata de la période de couverture peut être retenue.</p>
        
        <div class="exception-box">
          <i class="pi pi-info-circle"></i>
          <div>
            <h4>Exceptions</h4>
            <p>Le droit de rétractation ne s'applique pas aux contrats d'assurance :</p>
            <ul>
              <li>D'une durée inférieure à un mois</li>
              <li>Liés à un voyage ou bagage</li>
              <li>Entièrement exécutés à la demande expresse du Client</li>
            </ul>
          </div>
        </div>
      </div>
    </div>
  </section>

  <!-- Résiliation -->
  <section class="page-section">
    <h2><i class="pi pi-times-circle"></i> Résiliation des contrats</h2>
    
    <div class="termination-options">
      <div class="termination-card">
        <i class="pi pi-calendar"></i>
        <h3>À l'échéance annuelle</h3>
        <p>Le Client peut résilier son contrat à chaque échéance annuelle en respectant un préavis de 2 mois (sauf disposition contractuelle différente).</p>
      </div>
      
      <div class="termination-card">
        <i class="pi pi-calendar-plus"></i>
        <h3>Après 1 an (Loi Hamon)</h3>
        <p>Pour les contrats auto, habitation et affinitaires : résiliation possible à tout moment après 1 an d'ancienneté, sans frais ni pénalités.</p>
      </div>
      
      <div class="termination-card">
        <i class="pi pi-sync"></i>
        <h3>Changement de situation</h3>
        <p>Résiliation possible en cas de changement de situation (déménagement, mariage, retraite, etc.) sous conditions.</p>
      </div>
      
      <div class="termination-card">
        <i class="pi pi-chart-line"></i>
        <h3>Augmentation de prime</h3>
        <p>En cas d'augmentation de prime à l'échéance, possibilité de résilier dans les 15 jours suivant la notification.</p>
      </div>
    </div>
    
    <p class="assistance-note"><i class="pi pi-users"></i> Brokins vous assiste gratuitement dans vos démarches de résiliation et peut prendre en charge la procédure.</p>
  </section>

  <!-- Réclamations -->
  <section class="page-section">
    <div class="info-box">
      <h2><i class="pi pi-comment"></i> Réclamations et litiges</h2>
      
      <div class="complaint-procedure">
        <h3>Procédure de réclamation</h3>
        <p>En cas de désaccord ou de réclamation concernant nos services, nous vous invitons à suivre la procédure suivante :</p>
        
        <div class="complaint-step">
          <strong>Étape 1 : Contact direct</strong>
          <p>Contactez votre conseiller Brokins ou notre service client :</p>
          <ul>
            <li>Email : contact&#64;brokins.fr</li>
            <li>Téléphone : 01 43 27 12 43</li>
            <li>Courrier : BROKINS SAS - Service Réclamations, 79 Rue de Rennes, 75006 Paris</li>
          </ul>
          <p><strong>Délai de réponse :</strong> 10 jours ouvrés</p>
        </div>
        
        <div class="complaint-step">
          <strong>Étape 2 : Médiation</strong>
          <p>Si la réponse ne vous satisfait pas, vous pouvez saisir le Médiateur de l'Assurance :</p>
          <ul>
            <li>En ligne : www.mediation-assurance.org</li>
            <li>Par courrier : La Médiation de l'Assurance, TSA 50110, 75441 Paris Cedex 09</li>
          </ul>
          <p class="note">La saisine du médiateur est gratuite et peut être effectuée dans un délai d'un an après votre réclamation écrite.</p>
        </div>
        
        <div class="complaint-step">
          <strong>Étape 3 : Action judiciaire</strong>
          <p>En dernier recours, vous pouvez saisir les juridictions compétentes.</p>
        </div>
      </div>
    </div>
  </section>

  <!-- Responsabilité -->
  <section class="page-section">
    <h2><i class="pi pi-balance-scale"></i> Responsabilité et limites</h2>
    
    <div class="liability-info">
      <h3><i class="pi pi-shield"></i> Responsabilité de Brokins</h3>
      <p>BROKINS SAS s'engage à exercer son activité avec diligence et professionnalisme. Sa responsabilité est couverte par une assurance Responsabilité Civile Professionnelle.</p>
      
      <h3><i class="pi pi-exclamation-triangle"></i> Limitations</h3>
      <ul>
        <li>Brokins agit en qualité d'intermédiaire et ne se substitue pas aux assureurs dans leurs obligations contractuelles</li>
        <li>La responsabilité de Brokins ne peut être engagée que pour des fautes qui lui sont directement imputables</li>
        <li>Brokins ne garantit pas l'acceptation d'un dossier par les assureurs ni les conditions tarifaires proposées</li>
        <li>Les délais de traitement dépendent de la réactivité des assureurs partenaires</li>
      </ul>
      
      <h3><i class="pi pi-file-edit"></i> Responsabilité de l'assureur</h3>
      <p>Les conditions d'indemnisation, les garanties et les exclusions relèvent de la responsabilité exclusive de l'assureur. Le Client doit se référer aux conditions générales et particulières de son contrat.</p>
    </div>
  </section>

  <!-- Protection des données -->
  <section class="page-section">
    <div class="data-protection">
      <h2><i class="pi pi-lock"></i> Protection des données personnelles</h2>
      
      <p>La collecte et le traitement de vos données personnelles sont effectués dans le respect du RGPD et de la loi Informatique et Libertés.</p>
      
      <p>Pour plus d'informations sur la protection de vos données, consultez notre <strong><a [routerLink]="['/politique-confidentialite']">Politique de Confidentialité</a></strong>.</p>
    </div>
  </section>

  <!-- Droit applicable -->
  <section class="page-section">
    <h2><i class="pi pi-globe"></i> Droit applicable et juridiction</h2>
    
    <div class="jurisdiction-info">
      <p><strong>Droit applicable :</strong> Les présentes CGV sont régies par le droit français, notamment le Code des assurances et le Code de la consommation.</p>
      
      <p><strong>Langue :</strong> Les présentes CGV sont rédigées en français. En cas de traduction, seule la version française fait foi.</p>
      
      <p><strong>Juridiction compétente :</strong> En cas de litige, et après échec de la procédure de réclamation et de médiation, les tribunaux français seront seuls compétents.</p>
    </div>
  </section>

  <!-- Modification des CGV -->
  <section class="page-section">
    <div class="modification-notice">
      <h2><i class="pi pi-refresh"></i> Modification des CGV</h2>
      
      <p>BROKINS SAS se réserve le droit de modifier les présentes CGV à tout moment, notamment pour se conformer à toute évolution légale, réglementaire, jurisprudentielle ou technique.</p>
      
      <p>Les CGV applicables sont celles en vigueur à la date de la demande de service par le Client.</p>
      
      <p><strong>Toute modification substantielle sera portée à la connaissance des clients par email ou notification sur le site.</strong></p>
    </div>
  </section>

  <!-- Contact -->
  <section class="page-section">
    <div class="cta-box">
      <h3>Des questions sur nos conditions ?</h3>
      <p>Notre équipe est à votre disposition pour tout éclaircissement</p>
      <button class="btn-cta" (click)="navigateToContact()">
        <i class="pi pi-phone"></i> Contactez-nous
      </button>
    </div>
  </section>

</app-page-layout>